import { ErrorMessage } from '@wyz/ui';
import { Trans } from '~/common/context';
import {
  PurchaseTable,
  PurchaseTableHeader,
  PurchaseTyreGroupRow,
} from '~/purchase/components';

import { IBaseTyreOffer } from '@wyz/types';
import { isNil } from 'lodash-es';
import { memo, useCallback } from 'react';
import { IUserCustomer } from '~/common/types';
import { useManageSearch } from '~/purchase/api';
import { PurchaseSearchHelpers } from '~/purchase/helpers';
import { IOffer, IProductListState, ISearchTypeArgs } from '~/purchase/types';

type Props = {
  offers: IBaseTyreOffer[];
  customer: IUserCustomer;
  requestedQuantity: number;
  searchType: ISearchTypeArgs['searchType'];
  searchQuery: IProductListState['searchQuery'];
  onUpdateQuery: (tyreOffer: IBaseTyreOffer) => IBaseTyreOffer | undefined;
};
const Offers = ({
  offers,
  customer,
  requestedQuantity,
  searchType,
  searchQuery,
  onUpdateQuery,
}: Props) => {
  const manageSearch = useManageSearch<IBaseTyreOffer>({
    context: {
      customerId: customer.id,
      searchType,
    },
    refreshEan: { enabled: true },
  });

  const onRefreshTyre = useCallback(
    (ean: string) => {
      return new Promise<Array<IOffer>>((resolve, reject) => {
        if (isNil(searchQuery)) {
          return reject(new Error('No search query found'));
        }
        manageSearch.refreshEan.mutate(
          {
            ean: ean,
            baseQuery: searchQuery,
          },
          {
            onSuccess: (tyreOffer) => {
              if (isNil(tyreOffer) || isNil(searchQuery)) {
                return;
              }
              const updatedTyreOffer = onUpdateQuery(tyreOffer);
              if (updatedTyreOffer) {
                const mappedOffers: Array<IOffer> =
                  PurchaseSearchHelpers.mapOffers(updatedTyreOffer.suppliers);

                resolve(mappedOffers);
              }
            },
          },
        );
      });
    },
    [manageSearch.refreshEan, onUpdateQuery, searchQuery],
  );

  return (
    <>
      {offers.length === 0 ? (
        <ErrorMessage
          title={<Trans i18nKey='app.purchase.search_page.no_results' />}
        />
      ) : (
        <>
          <PurchaseTable>
            <PurchaseTableHeader />
          </PurchaseTable>
          {offers.map((product) => {
            return (
              <PurchaseTyreGroupRow
                key={product.tyre.ean}
                product={product}
                requestedQuantity={requestedQuantity}
                customer={customer}
                onRefreshTyre={onRefreshTyre}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default memo(Offers);
